import React, { useEffect, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString();
};

const formatAddress = (addr) => {
  if (!addr) return "-";
  const parts = [addr.addressLine1, addr.addressLine2, addr.zipcode].filter(
    Boolean,
  );
  return parts.length ? parts.join(", ") : "-";
};

function Pagination({ page, totalPages, onPageChange, disabled }) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <nav
      aria-label="Page navigation"
      className="mt-3 d-flex justify-content-center"
    >
      <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center gap-1">
        <li className={`page-item ${page <= 1 || disabled ? "disabled" : ""}`}>
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page - 1)}
          >
            <i className="bi bi-chevron-left" />
          </button>
        </li>
        {getPages().map((p, i) =>
          p === "..." ? (
            <li key={`ellipsis-${i}`} className="page-item disabled">
              <span className="page-link border-0 bg-transparent">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
              <button
                className="page-link rounded-3"
                onClick={() => onPageChange(p)}
                disabled={disabled}
              >
                {p}
              </button>
            </li>
          ),
        )}
        <li
          className={`page-item ${page >= totalPages || disabled ? "disabled" : ""}`}
        >
          <button
            className="page-link rounded-3"
            onClick={() => onPageChange(page + 1)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function AdminPaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [userIdInput, setUserIdInput] = useState("");
  const [appliedUserId, setAppliedUserId] = useState("");
  const [statusInput, setStatusInput] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");

  const adminToken = localStorage.getItem("adminToken");
  const activeFilterCount = [appliedUserId, appliedStatus].filter(
    Boolean,
  ).length;

  const fetchPayments = async (
    nextPage = 1,
    userId = appliedUserId,
    status = appliedStatus,
  ) => {
    setLoading(true);
    setErrorMsg("");

    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const qs = new URLSearchParams({
        page: String(nextPage),
        limit: String(limit),
      });
      if (userId.trim()) qs.set("userId", userId.trim());
      if (status) qs.set("status", status);

      const res = await fetch(
        `${API_BASE}/Payment/GetPaymentHistory?${qs.toString()}`,
        { headers: { Authorization: adminToken } },
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const result = await res.json();
      setPayments(result?.payments || []);
      setTotal(result?.total || 0);
      setPage(result?.page || nextPage);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to fetch payments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1, "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedUserId(userIdInput);
    setAppliedStatus(statusInput);
    setPage(1);
    fetchPayments(1, userIdInput, statusInput);
  };

  const handleClear = () => {
    setUserIdInput("");
    setAppliedUserId("");
    setStatusInput("");
    setAppliedStatus("");
    setPage(1);
    fetchPayments(1, "", "");
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchPayments(p, appliedUserId, appliedStatus);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Header />
      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">
                    Payment History ({total})
                  </h3>
                  <div className="text-muted">View All Transactions</div>
                </div>
                <div
                  className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                  onClick={() =>
                    fetchPayments(page, appliedUserId, appliedStatus)
                  }
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-dark" />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                  )}
                </div>
              </div>

              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-person me-1 text-muted" />
                        User ID
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={userIdInput}
                          onChange={(e) => setUserIdInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search by User ID…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={
                          loading ||
                          (!userIdInput &&
                            !appliedUserId &&
                            !statusInput &&
                            !appliedStatus)
                        }
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg && (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              )}

              <div className="card shadow-sm rounded-4">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>No.</th>
                          <th>Details</th>
                          <th>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="3" className="text-center py-5">
                              <span className="spinner-border spinner-border-sm" />
                            </td>
                          </tr>
                        ) : payments.length === 0 ? (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center py-5 text-muted"
                            >
                              No payments found.
                            </td>
                          </tr>
                        ) : (
                          payments.map((p, idx) => {
                            const lineItems = Array.isArray(p.items)
                              ? p.items
                              : [];
                            const tax = p.taxBreakup;

                            return (
                              <tr key={p.paymentId || idx}>
                                <td className="fw-semibold">
                                  {(page - 1) * limit + idx + 1}.
                                </td>
                                <td>
                                  <div className="lh-sm my-2">
                                    <div>
                                      <span className="text-muted">
                                        Payment ID
                                      </span>{" "}
                                      —{" "}
                                      <span className="fw-semibold">
                                        {p.paymentId}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        User ID
                                      </span>{" "}
                                      — {p.userId}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Name
                                      </span>{" "}
                                      — {p.name}
                                    </div>

                                    <div className="mt-2 mb-1 fw-semibold text-dark">
                                      Items ({lineItems.length})
                                    </div>
                                    <div className="border rounded-3 mb-2 overflow-hidden">
                                      <table className="table table-sm mb-0">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Product</th>
                                            <th>Product ID</th>
                                            <th>Qty</th>
                                            <th>Unit ₹</th>
                                            <th>Line Total ₹</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {lineItems.map((li, liIdx) => (
                                            <tr key={li.productId + liIdx}>
                                              <td className="text-capitalize">
                                                {li.productSnapshot?.name}
                                              </td>
                                              <td>{li.productId}</td>
                                              <td>{li.quantity}</td>
                                              <td>{li.price}</td>
                                              <td>{li.total}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div>
                                      <span className="text-muted">
                                        Subtotal
                                      </span>{" "}
                                      — ₹{p.subtotal}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Shipping Charge
                                      </span>{" "}
                                      — ₹{p.shippingCharge ?? 0}
                                    </div>
                                    {tax?.gstIncluded ? (
                                      <div>
                                        <span className="text-muted">
                                          GST ({tax.gstRate}%)
                                        </span>{" "}
                                        — ₹{tax.gstAmount}
                                      </div>
                                    ) : null}
                                    <div>
                                      <span className="text-muted">
                                        Total Paid
                                      </span>{" "}
                                      —{" "}
                                      <span className="fw-semibold">
                                        ₹{p.totalAmount}
                                      </span>{" "}
                                      {p.currency}
                                    </div>
                                    <div>
                                      <span className="text-muted">Status</span>{" "}
                                      —{" "}
                                      <span
                                        className={`badge text-capitalize ${p.status === "success" ? "bg-success" : "bg-warning text-dark"}`}
                                      >
                                        {p.status}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Paid At
                                      </span>{" "}
                                      — {formatDate(p.paidAt)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Shipping Address
                                      </span>{" "}
                                      — {formatAddress(p.shippingAddress)}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    <span className="text-muted">Order ID</span>{" "}
                                    — {p.razorpayOrderId}
                                  </div>
                                  <div>
                                    <span className="text-muted">
                                      Payment ID
                                    </span>{" "}
                                    — {p.razorpayPaymentId}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 gap-2">
                <div className="text-muted small">
                  Showing {payments.length ? (page - 1) * limit + 1 : 0}–
                  {Math.min(page * limit, total)} of {total} records
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminPaymentHistory;
